// 1099-NEC year-end PDF generator (IRS-style layout)
// Uses YTD payment data from /api/admin/payroll endpoint.
//
// IMPORTANT: This is NOT an official IRS-filable form. It is a reference
// statement formatted to MATCH the IRS 1099-NEC layout. Employers must still
// file the official copies with the IRS (Copy A, red ink) via paper or e-File
// (FIRE / IRIS). This PDF is suitable for:
//   - Copy B (recipient/contractor copy)
//   - Copy C (payer records)
//   - Internal year-end summaries
import jsPDF from 'jspdf';

const COMPANY = {
  name: 'BELLAJA Electrical & Security Systems LLC',
  address: 'Serving NYC, Connecticut, NJ',
  city_state_zip: 'New York, NY',
  phone: '(203) 803-7035',
  ein: 'XX-XXXXXXX',  // Default placeholder - overridden by company info from /api/site-content
};

/**
 * Fetch the company's tax settings (EIN, tax address) from site-content if
 * available, falling back to the static COMPANY defaults.
 */
async function loadPayerFromSite() {
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${API_URL}/api/site-content`);
    if (!res.ok) return COMPANY;
    const data = await res.json();
    const c = data?.company || {};
    return {
      name: COMPANY.name,
      address: c.tax_address || COMPANY.address,
      city_state_zip: c.tax_city_state_zip || COMPANY.city_state_zip,
      phone: c.phone || COMPANY.phone,
      ein: c.ein || COMPANY.ein,
    };
  } catch {
    return COMPANY;
  }
}

/**
 * Render one IRS-styled 1099-NEC box for a recipient.
 *
 * @param {Object} p
 * @param {Object} p.recipient    { name, email, address, ssn_or_tin }
 * @param {Object} p.payer        Defaults to COMPANY
 * @param {number} p.year         Tax year (e.g. 2026)
 * @param {number} p.nonemployee_compensation   Box 1 amount (YTD)
 * @param {number} [p.federal_withheld=0]       Box 4 (typically 0 for 1099)
 * @param {number} [p.ny_state_withheld=0]      State withholding (Box 5)
 * @param {string} [p.state_id='NY']
 * @returns {Promise<{blob:Blob, filename:string}>}
 */
export async function generate1099NecPdf({
  recipient,
  payer,
  year,
  nonemployee_compensation,
  federal_withheld = 0,
  ny_state_withheld = 0,
  state_id = 'NY',
}) {
  // If the caller didn't pass a payer, pull one from /api/site-content so the
  // admin's EIN + tax address flow into the form.
  if (!payer) payer = await loadPayerFromSite();
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const m = 14;

  // Header
  pdf.setFillColor(10, 10, 10);
  pdf.rect(0, 0, pageW, 22, 'F');
  pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(20);
  pdf.text('BELLAJA', m, 13);
  pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
  pdf.text('Electrical & Security Systems', m, 18);
  pdf.setTextColor(180, 180, 180); pdf.setFontSize(8);
  pdf.text(payer.phone, pageW - m, 13, { align: 'right' });
  pdf.text(new Date().toLocaleDateString(), pageW - m, 18, { align: 'right' });

  // Title
  pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
  pdf.text(`Form 1099-NEC`, m, 33);
  pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
  pdf.text(`Nonemployee Compensation Statement - Tax Year ${year}`, m, 38);
  pdf.setFontSize(7); pdf.setTextColor(150, 30, 30); pdf.setFont('helvetica', 'italic');
  pdf.text(
    'Reference copy for Recipient (Copy B). File official Copy A with the IRS via FIRE/IRIS.',
    m, 43
  );

  // Year box (top right - IRS-style)
  pdf.setDrawColor(20, 20, 20); pdf.setLineWidth(0.6);
  pdf.rect(pageW - m - 40, 28, 40, 16);
  pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20); pdf.setFontSize(8);
  pdf.text('TAX YEAR', pageW - m - 38, 33);
  pdf.setFontSize(20);
  pdf.text(String(year), pageW - m - 20, 41, { align: 'center' });

  // PAYER box
  let y = 52;
  pdf.setDrawColor(40, 40, 40); pdf.setLineWidth(0.3);
  pdf.rect(m, y, (pageW - m * 2) / 2 - 2, 30);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7); pdf.setTextColor(20, 20, 20);
  pdf.text("PAYER'S name, address, ZIP, and phone", m + 2, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(payer.name, m + 2, y + 10);
  pdf.setFontSize(8); pdf.setTextColor(60, 60, 60);
  pdf.text(payer.address, m + 2, y + 14.5);
  pdf.text(payer.city_state_zip, m + 2, y + 18.5);
  pdf.text(`Phone: ${payer.phone}`, m + 2, y + 22.5);
  pdf.setFontSize(7); pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'bold');
  const einDisplay = payer.ein && payer.ein !== 'XX-XXXXXXX' ? payer.ein : 'XX-XXXXXXX (set in Site Content → Tax Settings)';
  pdf.text(`PAYER'S TIN: ${einDisplay}`, m + 2, y + 27);

  // RECIPIENT box (right side)
  const rx = m + (pageW - m * 2) / 2 + 2;
  pdf.rect(rx, y, (pageW - m * 2) / 2 - 2, 30);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7); pdf.setTextColor(20, 20, 20);
  pdf.text("RECIPIENT'S name, address, ZIP", rx + 2, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(recipient.name, rx + 2, y + 10);
  pdf.setFontSize(8); pdf.setTextColor(60, 60, 60);
  if (recipient.address) {
    pdf.text(recipient.address, rx + 2, y + 14.5);
  }
  if (recipient.email) {
    pdf.text(recipient.email, rx + 2, y + 18.5);
  }
  pdf.setFontSize(7); pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'bold');
  pdf.text(
    `RECIPIENT'S TIN: ${recipient.ssn_or_tin || 'XXX-XX-XXXX (on file)'}`,
    rx + 2, y + 27,
  );

  // ===== Numbered boxes (IRS standard) =====
  y = 88;
  // Box 1 - Nonemployee compensation (the big one)
  const box1W = (pageW - m * 2) / 2 - 2;
  pdf.setLineWidth(0.4);
  pdf.rect(m, y, box1W, 18);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7); pdf.setTextColor(20, 20, 20);
  pdf.text('1  Nonemployee compensation', m + 2, y + 4);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18); pdf.setTextColor(0, 0, 0);
  pdf.text(`$${nonemployee_compensation.toFixed(2)}`, m + 4, y + 14);

  // Box 2 - Payer made direct sales totaling $5,000+ (blank for contractors)
  pdf.rect(m + box1W + 2, y, box1W, 18);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7);
  pdf.text('2  Payer made direct sales totaling', m + box1W + 4, y + 4);
  pdf.text('   $5,000+ for resale (check if yes)', m + box1W + 4, y + 8);
  pdf.setFontSize(11); pdf.text('[  ]', m + box1W + 4, y + 14);

  // Box 4 - Federal income tax withheld
  y += 22;
  pdf.rect(m, y, box1W, 14);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7);
  pdf.text('4  Federal income tax withheld', m + 2, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11);
  pdf.text(`$${federal_withheld.toFixed(2)}`, m + 4, y + 11);

  // Box 5/6/7 - State info
  pdf.rect(m + box1W + 2, y, box1W, 14);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7);
  pdf.text(`5  State tax withheld   |   6  State/Payer no.   |   7  State income`,
           m + box1W + 4, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(
    `$${ny_state_withheld.toFixed(2)}    |   ${state_id}    |   $${nonemployee_compensation.toFixed(2)}`,
    m + box1W + 4, y + 11,
  );

  // ===== Instructions =====
  y += 22;
  pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.2);
  pdf.line(m, y, pageW - m, y);
  y += 5;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(20, 20, 20);
  pdf.text('Recipient instructions', m, y); y += 5;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(60, 60, 60);
  const instructions = [
    `Box 1 reports your total nonemployee compensation paid by ${payer.name} for tax year ${year}.`,
    'As an independent contractor (1099-NEC), you are responsible for self-employment tax (SE tax),',
    'federal income tax, NY state income tax, and NYC city tax on this income. Quarterly estimated',
    'payments are typically required if you expect to owe $1,000 or more.',
    '',
    'Keep this form for your records and attach a copy to your federal Schedule C / SE filing.',
    '1099-NEC is required when nonemployee compensation is $600 or more (IRS rules).',
  ];
  instructions.forEach(line => { pdf.text(line, m, y); y += 4; });

  // Signature area
  y += 8;
  pdf.setDrawColor(40, 40, 40); pdf.line(m, y, m + 70, y);
  pdf.line(pageW - m - 50, y, pageW - m, y);
  pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
  pdf.text('Payer authorized signature', m, y + 3);
  pdf.text('Date', pageW - m - 50, y + 3);

  // Footer
  pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
  pdf.text(
    `Generated by BELLAJA payroll system on ${new Date().toLocaleString()}  |  Confidential`,
    pageW / 2, pageH - 8, { align: 'center' },
  );
  pdf.text(
    'NOT an official IRS filing - file Copy A with the IRS via FIRE / IRIS.',
    pageW / 2, pageH - 4, { align: 'center' },
  );

  const filename = `BELLAJA-1099NEC-${(recipient.name || 'Contractor').replace(/\s+/g, '_')}-${year}.pdf`;
  pdf.save(filename);
  return { blob: pdf.output('blob'), filename };
}
